import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, differenceInMinutes } from "date-fns";
import { Calendar, User, Bike, Clock, MapPin, Route } from "lucide-react";

interface BookingDetailsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  booking: {
    id: string;
    start_time: string;
    end_time: string;
    ride_type: string;
    status: string;
    start_location_type: string;
    end_location_type: string;
    start_location_id: string | null;
    end_location_id: string | null;
    user?: {
      username: string;
    };
    bike?: {
      serial_number: string;
    };
    start_site?: {
      name: string;
    };
    end_site?: {
      name: string;
    };
  } | null;
}

export const BookingDetailsDialog: React.FC<BookingDetailsDialogProps> = ({
  isOpen,
  onOpenChange,
  booking,
}) => {
  if (!booking) return null;

  const startTime = new Date(booking.start_time);
  const endTime = new Date(booking.end_time);
  const duration = differenceInMinutes(endTime, startTime);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "unconfirmed":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "unavailable":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRideTypeColor = (rideType: string) => {
    switch (rideType) {
      case "work":
        return "bg-blue-100 text-blue-800";
      case "commute":
        return "bg-green-100 text-green-800";
      case "fitness":
        return "bg-purple-100 text-purple-800";
      case "quick":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getLocationDisplay = (locationType: string, site?: { name: string } | null) => {
    if (locationType === "site") {
      return site?.name || "Unknown Site";
    }
    return "Home";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Route className="h-5 w-5" />
            Booking Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date and Time */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Date</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {format(startTime, "dd MMM yyyy")}
              </div>
              <div className="text-xs text-muted-foreground">
                {format(startTime, "HH:mm")} - {format(endTime, "HH:mm")}
              </div>
            </div>
          </div>

          {/* User */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">User</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {booking.user?.username || "Unknown User"}
              </div>
            </div>
          </div>

          {/* Bike */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Bike className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Bike</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {booking.bike?.serial_number || "Unknown Bike"}
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Duration</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {duration} minutes
              </div>
            </div>
          </div>

          {/* Ride Type */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Route className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Ride Type</span>
            </div>
            <div className="ml-6">
              <Badge className={getRideTypeColor(booking.ride_type)}>
                {booking.ride_type || "Unknown"}
              </Badge>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-muted" />
              <span className="font-medium">Status</span>
            </div>
            <div className="ml-6">
              <Badge className={getStatusColor(booking.status)}>
                {booking.status || "Unknown"}
              </Badge>
            </div>
          </div>

          {/* Start Location */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Start Location</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {getLocationDisplay(booking.start_location_type, booking.start_site)}
              </div>
            </div>
          </div>

          {/* End Location */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">End Location</span>
            </div>
            <div className="ml-6">
              <div className="text-sm font-medium">
                {getLocationDisplay(booking.end_location_type, booking.end_site)}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 